import PropTypes from 'prop-types'
import { Receipt, Warning } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { EMPTY_VALUE } from '@/constants'
import { formatPrice } from '@/utils'

import Loader from '../../Loader/Loader'

import { isAchPaymentType, paymentTypeLabel } from './helpers'

// Maps a record's status to the modifier class that colors its badge.
const STATUS_BADGE_CLASSES = {
  Disputed: 'is-disputed',
  Failed: 'is-failed',
  Paid: 'is-paid',
  Processing: 'is-processing',
  Refunded: 'is-refunded',
}

const PaymentHistory = ({ history, isLoading, error, isPmsHistoryUnavailable }) => {
  const { t } = useTranslation(['organisation'])

  const isEmpty = !error && !isLoading && history.length === 0

  return (
    <div className="wrapper-paymentHistory">
      <h2 className="ctp-payments__header">{t('organisation:payments.history.title')}</h2>
      {isLoading && <Loader className="loader" />}
      {error && <div className="error-notification">{error}</div>}

      {isEmpty && !isPmsHistoryUnavailable && <div>{t('organisation:payments.history.empty')}</div>}

      {isEmpty && isPmsHistoryUnavailable && (
        <div className="paymentHistory_pmsUnavailable">
          <img
            className="pmsUnavailable-image"
            src="/images/no-results.svg"
            alt={t('organisation:payments.history.pmsUnavailable.imageAlt')}
          />
          <div className="pmsUnavailable-content">
            <h3 className="pmsUnavailable-title">
              {t('organisation:payments.history.pmsUnavailable.title')}
            </h3>
            <p className="pmsUnavailable-description">
              {t('organisation:payments.history.pmsUnavailable.description')}
            </p>
          </div>
        </div>
      )}

      <div className="paymentHistory_items">
        {history.map(({ dateGroup, items }) => (
          <div key={dateGroup} className="paymentHistory_dateGroup">
            <div className="dateGroup">{dateGroup}</div>
            {items.map((item) => (
              <div key={item.id} className="paymentHistory_item">
                <div className="descriptionColumn">
                  {item.charges.map((charge, index) => (
                    <div key={charge.id} className="rowDescription">
                      <div className="chargeLabel">
                        <Receipt className="receipt-icon" size={24} />
                        <div className="description">
                          {charge.label}
                          {index === 0 && item.isAutoPay
                            ? t('organisation:payments.history.autopaySuffix')
                            : ''}
                        </div>
                      </div>
                      {charge.amountCents != null && (
                        <div className="chargeAmount">{`-${formatPrice(charge.amountCents)}`}</div>
                      )}
                    </div>
                  ))}
                  {isAchPaymentType(item.paymentType) && item.status === 'Processing' && (
                    <div className="achProcessingNotice">
                      <Warning className="achProcessingNotice-icon" size={20} />
                      <div className="achProcessingNotice-text">
                        {t('organisation:payments.history.achProcessingNotice')}
                      </div>
                    </div>
                  )}
                </div>
                <div className="statusColumn">
                  <div className={`status-badge ${STATUS_BADGE_CLASSES[item.status] || ''}`}>
                    {item.status}
                  </div>
                </div>
                <div className="paymentTypeColumn">
                  <div className="paymentTypeLabel">
                    {t('organisation:payments.history.paymentType.label')}
                  </div>
                  <div className="paymentTypeValue">{paymentTypeLabel(t, item.paymentType)}</div>
                </div>
                <div className="feeColumn">
                  <div className="total-header">
                    {t('organisation:payments.history.processingFee')}
                  </div>
                  <div className="feeValue">{item.processingFee ?? EMPTY_VALUE}</div>
                </div>
                <div className="rowAmount">
                  <div className="total-header">{t('organisation:payments.history.amount')}</div>
                  <div className="amountValue">{item.totalAmount}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

PaymentHistory.propTypes = {
  history: PropTypes.arrayOf(
    PropTypes.shape({
      dateGroup: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          charges: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              amountCents: PropTypes.number,
              label: PropTypes.string,
            }),
          ),
          totalAmount: PropTypes.string,
          processingFee: PropTypes.string,
          status: PropTypes.string,
          isAutoPay: PropTypes.bool,
          paymentType: PropTypes.string,
        }),
      ),
    }),
  ),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  isPmsHistoryUnavailable: PropTypes.bool,
}

export default PaymentHistory
